import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Search, X } from 'lucide-react'
import type { Column } from '@tanstack/react-table'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import type { FilterValue } from '@/shared/lib/api/api-types'

interface DateFilterProps<TData, TValue> {
  column: Column<TData, TValue>
  onClose: () => void
}

const DATE_TYPES = [
  { value: 'equals', labelKey: 'dataTable.operators.equals' },
  { value: 'greaterThan', labelKey: 'dataTable.operators.after' },
  { value: 'lessThan', labelKey: 'dataTable.operators.before' },
  { value: 'inRange', labelKey: 'dataTable.operators.between' },
]

export function DateFilter<TData, TValue>({ column, onClose }: DateFilterProps<TData, TValue>) {
  const { t } = useTranslation()
  const current = column.getFilterValue() as Extract<FilterValue, { filterType: 'date' }> | undefined
  const [type, setType] = useState(current?.type ?? 'inRange')
  const [dateFrom, setDateFrom] = useState(current?.dateFrom ?? '')
  const [dateTo, setDateTo] = useState(current?.dateTo ?? '')

  function apply() {
    if (!dateFrom) {
      column.setFilterValue(undefined)
      onClose()
      return
    }
    const filter: Extract<FilterValue, { filterType: 'date' }> = {
      filterType: 'date',
      type,
      dateFrom,
      ...(type === 'inRange' && dateTo ? { dateTo } : {}),
    }
    column.setFilterValue(filter)
    onClose()
  }

  return (
    <div className="flex flex-col gap-2 p-2 w-52">
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="h-7 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DATE_TYPES.map((option) => (
            <SelectItem key={option.value} value={option.value} className="text-xs">
              {t(option.labelKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('dataTable.from')}</Label>
        <Input
          type="date"
          className="h-7 text-xs"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          autoFocus
        />
      </div>
      {type === 'inRange' && (
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">{t('dataTable.to')}</Label>
          <Input
            type="date"
            className="h-7 text-xs"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      )}
      <div className="flex gap-1">
        <Button size="sm" className="h-6 flex-1 text-xs" onClick={apply}>
          <Search className="mr-1 h-3 w-3" /> {t('dataTable.apply')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          onClick={() => { column.setFilterValue(undefined); onClose() }}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}
